import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { Link as LinkComponent } from "../../components/atoms/Link";
import colours from "../../lib/colours";
import { ReactComponent as ArrowIcon } from "../../assets/svg/arrow-right-long.svg";
import Footer from "../Other/Footer";
import appConfig from "../../lib/appConfig";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import FilledButton from "../../components/atoms/FilledButton";
import { ReactComponent as ArrowRightIcon } from "../../assets/svg/arrow-right.svg";
import { firstPartyOffsetGuide } from "../../lib/constants";

const Container = styled.div`
  height: calc(100vh - ${appConfig.headerHeight}px);
  display: flex;
  flex-direction: column;
  font-weight: 300;
  color: ${colours.darkerGrey};
`;

const SubHeader = styled.div`
  background-color: ${colours.th1l1alt};
  color: ${colours.white};
`;

const FluidContainer = styled.div`
  max-width: ${appConfig.homePageContentWidth};
  margin: auto;
  padding-inline: 1rem;
`;

const SubHeaderInnerContainer = styled(FluidContainer)`
  padding-block: 1rem;
`;

const SubHeaderLinkContainer = styled.div`
  height: 69px;
  box-sizing: border-box;
  padding-top: 8px;
`;

const SubHeaderLinkInnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BackLink = styled(Link)`
  padding: 2px 0;
  font-weight: 400;
  font-size: 14px;
  color: ${colours.white};
  display: inline;
  text-decoration: none;
  &: hover {
    text-decoration: underline;
  }
`;

const StyledBackArrow = styled(ArrowIcon)`
  position: relative;
  top: -2px;
  width: 1.6875rem;
  height: 0.875rem;
  transform: rotate(180deg);
  margin-right: 12px;
`;

const SubHeaderText = styled.h2`
  font-size: 2.1875rem;
  font-weight: 500;
  margin-block: -2px 4px;
`;

const MainContent = styled.div`
  flex: 1;
`;

const MainContentInner = styled(FluidContainer)`
  margin-block: 1.8rem;
  padding-bottom: 80px;
`;

const Header3 = styled.h3`
  font-size: 28px;
  font-weight: 600;
  color: ${colours.th1};
  margin-block: 60px 20px;
`;

const Header4 = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: ${colours.th1};
  margin-block: 40px 16px;
`;

const Header5 = styled.h5`
  font-size: 17px;
  font-weight: 500;
  color: ${colours.th1};
  margin-block: 32px 8px;
`;

const MediumSpan = styled.span`
  font-weight: 500;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-block: 48px 36px;
`;

const ActionButton = styled(FilledButton)`
  display: flex;
  align-items: center;
  height: auto;
  padding: 14px 40px;
  min-width: min(80vw, 386px);
  justify-content: center;
  padding-block: 24px;
  width: min(500px, 100%);
  font-size: 18px;
  transition: background-color 0.6s;
`;

const ButtonArrowIcon = styled(ArrowRightIcon)`
  height: 14px;
  margin-left: 10px;
`;

const ItalicSpan = styled.span`
  font-style: italic;
`;

function OffsetHome() {
  const navigate = useNavigate();

  return (
    <Container>
      <SubHeader>
        <SubHeaderInnerContainer>
          <SubHeaderLinkContainer>
            <SubHeaderLinkInnerContainer>
              <StyledBackArrow />
              <BackLink to="/">NVR Map</BackLink>
            </SubHeaderLinkInnerContainer>
          </SubHeaderLinkContainer>
          <SubHeaderText>Proposal to protect native vegetation</SubHeaderText>
        </SubHeaderInnerContainer>
      </SubHeader>
      <MainContent>
        <MainContentInner>
          <Header3>Native Vegetation Offset Tool</Header3>
          <p>
            Under Victoria’s Native Vegetation Regulations an offset must be
            secured before native vegetation can be removed. As an applicant,
            you have two options: purchasing a third-party offset from someone
            else or establishing a first-party offset by protecting and managing
            native vegetation on your own land forever (noting these obligations
            would transfer to all new landowners of the land forever).
          </p>
          <p>
            This tool generates a Native Vegetation Offset Report, which assists
            in establishing a{" "}
            <MediumSpan>first-party general offset site</MediumSpan>. Using the
            information you provide; the report will determine the number of
            General Habitat Units (GHUs) associated with your proposed offset
            site. Once the offset is secured, these GHUs can be used to offset
            native vegetation removal on your property and meet the necessary
            conditions of approval.
          </p>
          <p>
            The process of establishing a first-party general offset site, along
            with the eligibility requirements, is outlined in the{" "}
            <LinkComponent
              href={firstPartyOffsetGuide}
              target="_blank"
              title="First Party Offset Guide"
              rel="noopener noreferrer"
            >
              First Party Offset Guide
            </LinkComponent>
            <ExternalLinkIcon />. To secure such a site, you must enter into an
            on-title security agreement and commit to specific management
            actions aimed at improving the security and condition of native
            vegetation.{" "}
            <MediumSpan>
              The security agreement and the obligation to preserve the achieved
              gains are perpetual, extending indefinitely into the future.
            </MediumSpan>
          </p>
          <ActionButtonContainer>
            <ActionButton onClick={() => navigate("/map/offset")}>
              <span>Launch tool</span>
              <ButtonArrowIcon />
            </ActionButton>
          </ActionButtonContainer>
          <Header4>Before Using This Tool</Header4>
          <p>
            We recommend reviewing the{" "}
            <LinkComponent
              href={firstPartyOffsetGuide}
              target="_blank"
              title="First Party Offset Guide"
              rel="noopener noreferrer"
            >
              First Party Offset Guide
            </LinkComponent>
            <ExternalLinkIcon /> and consulting with the regulator of your
            preferred security mechanism to gauge their interest in entering
            into an agreement. Depending on your chosen agreement type, you may
            wish to contact:
          </p>
          <ul>
            <li>
              Your local council for an agreement under Section 173 of the{" "}
              <ItalicSpan>Planning and Environment Act 1987</ItalicSpan>
            </li>
            <li>
              The Department of Energy, Environment and Climate Action (DEECA)
              for an agreement under Section 69 of the{" "}
              <ItalicSpan>Conservation, Forests and Lands Act 1987</ItalicSpan>
            </li>
            <li>
              Trust for Nature for an agreement under the{" "}
              <ItalicSpan>Victorian Conservation Trust Act 1972</ItalicSpan>.
            </li>
          </ul>
          <p>
            Securing a first-party general offset site incurs various costs,
            including setting up the security agreement, completion of ongoing
            management actions, potential income loss from foregoing the use and
            development of the land and associated potential losses in land
            value. It is essential to accurately estimate these costs before
            establishing a first-party offset site.{" "}
            <MediumSpan>
              Purchasing an offset from a third party may be a more
              cost-effective option when considering all costs associated with
              the protection and management of the site forever.
            </MediumSpan>
          </p>
          <Header5>This tool cannot be used to:</Header5>
          <ul>
            <li>
              Establish a third-party offset site (one that generates units sold
              to others)
            </li>
            <li>Generate Species Habitat Units (SHUs) of gain at the site</li>
            <li>Establish an offset on Crown Land.</li>
          </ul>
          <Header5>
            Using this tool, you can protect the following as an offset site:
          </Header5>
          <ul>
            <li>
              A Patch of native vegetation, noting that you need to measure any
              Patch Trees you wish to protect.
            </li>
            <li>
              A Scattered Tree that is at least 75 percent of the Large Tree
              benchmark size for the relevant bioregional Ecological Vegetation
              Class (EVC). You will need to measure any Scattered Trees you want
              to protect.
            </li>
            <li>
              An area of woody revegetation that meets the minimum area to
              perimeter ratio requirements outlined in the{" "}
              <LinkComponent
                href={firstPartyOffsetGuide}
                target="_blank"
                title="First Party Offset Guide"
                rel="noopener noreferrer"
              >
                First Party Offset Guide
              </LinkComponent>
              <ExternalLinkIcon />.
            </li>
          </ul>
        </MainContentInner>
      </MainContent>
      <Footer />
    </Container>
  );
}

export default OffsetHome;
